import _ from "underscore";
import m from "mithril";
import {ItemGroup} from "./itemGroup";
import {GroupRepository} from "./groupRepository";
import {XmlTagInfo} from "../xmlTagProvider";
import {Form} from "../form/form";
import {XmlUtils} from "../utils";
import {DragValue, DragItem} from "../items/dragdrop/dragItem";
import {DragSourceItem} from "../items/dragdrop/dragSourceItem";
import EventEmitter = require('wolfy87-eventemitter');

type DragValueOrNullArr = (DragValue|null)[];

export class DragGroup extends ItemGroup<DragItem> {
    private _acceptSourceIds: string[];
    private _events = new EventEmitter();
    private _dragValues?: DragValue[];
    private _unique: string;
    private _type: string;
    private _dropBehavior: string;

    constructor(config: XmlTagInfo, form: Form) {
        super(config, form);
        this._acceptSourceIds = XmlUtils.childrenByName(config, "acceptedSource").map(n => n.attributes['group']);
        this._type = config.attributes['type'];
        this._unique = config.attributes['unique'];
        this._dropBehavior = config.attributes['dropBehavior'];

        this.events.on(DragGroup.Events.DRAG_START, (p:any, dragValue:DragValue) => {

            if( this._unique === DragGroup.Unique.CLONED ) {
                if( dragValue.location && dragValue.location.group === this) {
                    this.dragValues().push(DragValue.clone(dragValue));
                }
            }
        });
    }

    getAcceptSourceGroups(): DragGroup[] {
        return <DragGroup[]>this._acceptSourceIds.map(groupId => this.form.groupById(groupId))
            .filter(group => group instanceof DragGroup);
    }

    /**
     * Get drag values of this group and all acceptSource groups
     */
    getDragValuesTransitive(): DragValue[] {
        const transitive = this.getAcceptSourceGroups()
            // @ts-ignore create flat map of DragValues
            .reduce((res, g) => [...res, ...g.dragValues()], []); // flatMap
        // @ts-ignore transitive is array
        return [...this.dragValues(), ...transitive];
    }

    get events() {
        return this._events;
    }

    handleDrop(dragItem: DragItem, dragValue: DragValue) {
        const oldGroup = dragValue.location.dragGroup;
        // Drag cloned item to a source group to remove it
        if( this._type === DragGroup.Type.SOURCE) {
            dragValue.reset();
            m.redraw();
            oldGroup.notifyDragValuesUpdate();

            return;
        }
        // m.redraw.strategy('all');

        if( this._dropBehavior === DragGroup.DropBehavior.REPLACE ) {
            if (dragItem.droppedValue) {
                dragItem.droppedValue.reset();
            }
            dragValue.set(dragItem);
            m.redraw();
        }
        if( this._dropBehavior === DragGroup.DropBehavior.SHIFT ) {
            let arr: DragValueOrNullArr = this.dragValuesAsArray();
            let insertPos = this.cellItems.indexOf(dragItem);

            // Move of item within group downwards -> insert it after dropped item (not before)
            let currentItemPos = arr.indexOf(dragValue);
            if( currentItemPos >= 0 && currentItemPos < insertPos ) {
                insertPos++;
            }
            // remove current item - when item is moved within the same group
            arr = arr.map(dv => dv === dragValue ? null : dv);
            arr.splice(insertPos, 0, dragValue); // insert value
            arr = arr.filter(dv => dv); // remove null values
            // Truncate to cellItems size and reset extra elements
            arr.splice(this.cellItems.length).forEach( (dv: DragValue|null) => {dv && dv.reset();} );
            this.updateDragValues(arr);
        }
        oldGroup.notifyDragValuesUpdate();
    }

    /**
     * This is called only on SOURCE group
     */
    notifyDragValueReset(dragValue: DragValue) {
        if( this._unique === DragGroup.Unique.CLONED && this._dragValues) {
            this._dragValues = _.without(this._dragValues, dragValue);
        }
    }
    notifyDragValuesUpdate() {
        if( this._dropBehavior === DragGroup.DropBehavior.SHIFT ) {
            const arr = this.dragValuesAsArray()
                .filter(dv => dv); // remove null values;
            this.updateDragValues(arr);
        }
    }

    /**
     * Get array of DragValues indexed by cellItem position
     * @internal
     * @returns {DragValue[]}
     */
    public dragValuesAsArray() : DragValueOrNullArr {
        // Map drag values to array according to cellItems
        let arr = <DragValueOrNullArr>this.getDragValuesTransitive()
            .map(dv => [dv, this.cellItems.indexOf(dv.location)])
            .filter(([, i]) => i >= 0)
            .reduce((res, [dv, i]) => {
                res[<number>i] = dv;
                return res;
            }, []);
        // Replace undefined to nulls and expand to cellItems list size
        for (let i = 0; i < this.cellItems.length; i++) {
            if (arr[i] === undefined) {
                arr[i] = null;
            }
        }
        return arr;
    }

    /**
     * Update DragValues according to array
     * @param arr indexed by cellItem position
     */
    private updateDragValues(arr?: DragValueOrNullArr) {

        for( let i = 0; i < this.cellItems.length; i++ ) {
            this.cellItems[i].reset();
        }
        if (arr) {
            for( let i = 0; i < arr.length; i++ ) {
                const dragValue = arr[i];
                if(dragValue) {
                    dragValue.set(this.cellItems[i]);
                }
            }
        }
        m.redraw();
    }



    private createAcceptSourceDragValues(): DragValue[] {
        return this.cellItems
            .filter(ci => ci instanceof DragSourceItem )
            // @ts-ignore its fine - filter only DragSourceItems
            .map((ci: DragSourceItem) => ci.createDragValue());
    }

    /**
     * Lazy create of DragValues
     */
    private dragValues(): DragValue[] {
        if(!this._dragValues) {
            this._dragValues = this.createAcceptSourceDragValues();
        }
        return this._dragValues;
    }

}

// @ts-ignore
GroupRepository.register('dragGroup', DragGroup);


export module DragGroup {
    export class Events {
        public static DRAG_START = 'dragStart';
        public static DRAG_MOVE = 'dragMove';
        public static DRAG_DROP = 'dragDrop';
    }

    export class Unique {
        public static UNIQUE = 'UNIQUE';
        public static CLONED = 'CLONED';
    }
    export class Type {
        public static SOURCE = 'source';
        public static TARGET = 'target';
    }
    export class DropBehavior {
        public static REPLACE = 'REPLACE';
        public static SHIFT = 'SHIFT';
    }
}